import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Input,
  Menu,
  Popover,
  Tooltip,
  Tree
} from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg';
import filterOutlined from 'assets/filter_outlined.svg';
import LoadingSpinner from 'components/LoadingSpinner';
import {
  filterNodesBySearchFunction,
  obtainAllKeys
} from 'screens/Reports/helpers/helperFunctions';
import SearchIcon from 'components/Icons/SearchIcon';
import EmptyStateTree from './EmptyStateTree';
import './index.css';

const checklistTreeDefaultPropTreeData = [];
const checklistTreeDefaultPropSetNumResults = () => {};
const checklistTreeDefaultPropEmptyStateConfig = {
  title: '',
  message: '',
  overlayIcon: ''
};
const checklistTreeDefaultPropOnOpenFilters = () => {};
const checklistTreeDefaultPropHasActiveFilters = () => {};
const checklistTreeDefaultPropGroupByOptions = [];
const ChecklistTree = ({
  treeData = checklistTreeDefaultPropTreeData,
  hasActiveFilters = checklistTreeDefaultPropHasActiveFilters,
  loading = false,
  loadingLabel = 'Loading',
  checked,
  setChecked,
  setNumResults = checklistTreeDefaultPropSetNumResults,
  onOpenFilters = checklistTreeDefaultPropOnOpenFilters,
  enableFilters = false,
  emptyStateConfig = checklistTreeDefaultPropEmptyStateConfig,
  initialSelectAll = true,
  groupBy = '',
  onGroupByChange = null,
  groupByOptions = checklistTreeDefaultPropGroupByOptions
}) => {
  const [expanded, setExpanded] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filteredTreeData, setFilteredTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const { title, message, overlayIcon } = emptyStateConfig;
  const dropdownRef = useRef(null);

  const handleSelectAll = () => {
    if (checked.length === checkedKeys.length) {
      setChecked([]);
    } else {
      setChecked(checkedKeys);
    }
  };

  const onFilterChange = e => {
    const searchText = e.target.value;
    if (searchText?.length < 3) {
      setFilteredTreeData(treeData);
      return;
    }

    const filterNodes = filterNodesBySearchFunction(searchText);
    // Called through an arrow rather than passed to reduce directly: reduce
    // also supplies the index and the source array, which filterNodes does not
    // take.
    setFilteredTreeData(
      treeData.reduce((filtered, node) => filterNodes(filtered, node), [])
    );
  };

  const isEmptyStateTree = useMemo(
    () => (title.length ? treeData : treeData.length),
    [title, treeData]
  );

  useEffect(() => {
    if (isEmptyStateTree && !loading) {
      setFilteredTreeData(treeData);
      setInitialLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  useEffect(() => {
    setNumResults(filteredTreeData.length);

    const newCheckedKeys = obtainAllKeys(filteredTreeData);
    setCheckedKeys(newCheckedKeys);

    if (!initialSelectAll) return;

    if (initialLoad && filteredTreeData.length) {
      setChecked(newCheckedKeys);
      setInitialLoad(false);
      return;
    }
    setChecked(newCheckedKeys.filter(id => checked.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTreeData]);

  const handleGroupByChange = value => {
    onGroupByChange(value);
    setShowGroupByDropdown(false);
  };

  const isAllSelected = checked.length === checkedKeys.length;
  const isIndeterminate =
    checked.length > 0 && checked.length < checkedKeys.length;
  return (
    <div className="filter-container">
      {!loading ? (
        <>
          {isEmptyStateTree ? (
            <>
              <div className="mt-4 mb-2 flex flex-row items-center">
                <div className="grow">
                  <Input
                    className="w-full"
                    prefix={<SearchIcon />}
                    placeholder="Search properties"
                    type="search"
                    onChange={onFilterChange}
                  />
                </div>
                {onGroupByChange &&
                  groupByOptions &&
                  groupByOptions.length > 1 && (
                    <div className="relative ml-2" ref={dropdownRef}>
                      <Popover
                        trigger="click"
                        placement="bottomRight"
                        showArrow={false}
                        destroyTooltipOnHide
                        open={showGroupByDropdown}
                        onOpenChange={setShowGroupByDropdown}
                        getPopupContainer={() =>
                          dropdownRef.current ?? document.body
                        }
                        overlayClassName="checklist-tree-group-menu"
                        content={
                          <Menu
                            selectedKeys={[groupBy]}
                            onClick={({ key }) => handleGroupByChange(key)}
                            items={groupByOptions.map(option => ({
                              key: option.value,
                              label: (
                                <>
                                  <svg
                                    aria-hidden="true"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    className={`mr-2 ${
                                      groupBy === option.value
                                        ? ''
                                        : 'opacity-0'
                                    }`}
                                  >
                                    <path
                                      fill="#0071cd"
                                      d="M9 16.17 5.12 12.29 3.71 13.7 9 19l12-12-1.41-1.41z"
                                    />
                                  </svg>
                                  {option.label}
                                </>
                              )
                            }))}
                          />
                        }
                      >
                        <button
                          data-testid="group-by-button"
                          type="button"
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 bg-white border border-neutral-30"
                          aria-label="Group by"
                          aria-expanded={showGroupByDropdown}
                        >
                          <Tooltip title="Group by">
                            <img src={SortIcon} alt="" />
                          </Tooltip>
                        </button>
                      </Popover>
                    </div>
                  )}
                {enableFilters && (
                  <div className="relative ml-2">
                    <button
                      data-testid="panel-filter-button"
                      type="button"
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 bg-white border border-neutral-30"
                      onClick={onOpenFilters}
                    >
                      <img src={filterOutlined} alt="" />
                    </button>
                    {hasActiveFilters() && (
                      <div
                        className="w-10px h-10px rounded-full bg-blue-90 absolute top-0"
                        style={{ right: '-4px' }}
                      />
                    )}
                  </div>
                )}
              </div>
              {filteredTreeData.length ? (
                <>
                  <div className="mt-2 mb-1 flex">
                    <Checkbox
                      data-testid="checklist-tree-select-all"
                      size="small"
                      onChange={handleSelectAll}
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                    >
                      Select All
                    </Checkbox>
                  </div>
                  <Tree
                    checkable
                    treeData={filteredTreeData}
                    checkedKeys={checked}
                    onCheck={setChecked}
                    expandedKeys={expanded}
                    onExpand={setExpanded}
                    selectable={false}
                    showIcon={false}
                  />
                </>
              ) : (
                <div>
                  {title.length ? (
                    <EmptyStateTree
                      title={title}
                      message={message}
                      overlayIcon={overlayIcon}
                    />
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </>
      ) : (
        <div className="mt-40px">
          <LoadingSpinner message="" size={50} inline label={loadingLabel} />
        </div>
      )}
    </div>
  );
};

ChecklistTree.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  checked: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChecked: PropTypes.func.isRequired,
  setNumResults: PropTypes.func,
  emptyStateConfig: PropTypes.shape(),
  onOpenFilters: PropTypes.func,
  hasActiveFilters: PropTypes.func,
  enableFilters: PropTypes.bool,
  initialSelectAll: PropTypes.bool,
  groupBy: PropTypes.string,
  onGroupByChange: PropTypes.func,
  groupByOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

export default ChecklistTree;
